import React, {useMemo, useState} from 'react';
import makeStyles from "@material-ui/core/styles/makeStyles";
import useTemplateInstance from "../../hooks/useTemplateInstance";
import {Trans, useTranslation} from "react-i18next";
import {calculateCropRect, getBoxSize,  cropRectToImagePosition} from "../../../../utils";
import {useRecentImages} from "../../hooks/recentimages";
import EpmsImage from "../../../Common/EpmsImage";
import {getImageResourceSize} from "../../../Providers/TemplateInstanceProvider";
import useTheme from "@material-ui/core/styles/useTheme";
import {fade} from "@material-ui/core/styles/colorManipulator";
import Icon from "@material-ui/core/Icon";
import {Dialog} from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import {imageQualityWarning} from "../../../../lib/imageutils";

const useStyles = makeStyles(theme => ({
    renderPart: {
        position: 'absolute',
        boxSizing: 'border-box',
        overflow: 'hidden',
        '&.loading > div': {
            animation: 'image-pulse 2s infinite',
            filter: 'blur(4px)'
        }
    },
    image: {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    selectText: {
        position: 'absolute',
        display: 'flex',
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '10px',
        color: theme.palette.getContrastText(theme.palette.primary.main),
        textAlign: 'center'
    },
    selectImage: {
        position: 'absolute',
        display: 'flex',
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '16px',
        color: theme.palette.getContrastText(theme.palette.primary.main),
        backgroundColor: fade(theme.palette.primary.main, 0.27),
        fontWeight: 'bold',
    },
    "@global": {
        "@keyframes image-pulse": {
            "0%": {
                filter: 'blur(4px)'
            },
            "70%": {
                filter: 'blur(2px)'
            },
            "100%": {
                filter: 'blur(4px)'
            }
        }
    },
    qualityWarning: {
        position: 'absolute',
        right: '5px',
        bottom: '5px',
        color: 'yellow',
        padding: 0
    }
}))

const RenderImage = (props) => {
    const classes = useStyles ();

    if (props.resource.userSupplied) {
        return <RenderUserImage {...props} />
    }

    const {renderPart, resource, scaleRatio} = props;

    const scaledRenderPart = useMemo(() => {
        return {
            width: renderPart.size.width * scaleRatio,
            height: renderPart.size.height * scaleRatio,
            x: renderPart.position.x * scaleRatio,
            y: renderPart.position.y * scaleRatio,
        }
    }, [renderPart, scaleRatio])

    const styles = {
        width: scaledRenderPart.width + 'px',
        height: scaledRenderPart.height + 'px',
        top: scaledRenderPart.y + 'px',
        left: scaledRenderPart.x + 'px',
    }

    let url = null;
    // Template resource:
    if (resource.image && resource.image.url) {
        url = resource.image.url;
    }
    else if (resource.image && resource.image.uuid) {
        url = '/system/XPhoto.php?uuid=' + resource.image.uuid;
        if (resource.image.effect == 'bw') {
            url += '&type=1'
        }
        else if (resource.image.effect == 'sepia') {
            url += '&type=2'
        }

        if (resource.image.rotation) {
            url += '&rotation=' + resource.image.rotation;
        }
    }

    const imageStyle = {
        objectFit: renderPart.image.adaption == 'fill-in' ? 'cover' : 'contain'
    }

    return <div className={classes.renderPart} style={styles}>
        {url && <img src={url} className={classes.image} style={imageStyle}/>}
        {!url && <div><Trans>Choose image</Trans></div>}
    </div>
}

export default RenderImage;

const RenderUserImage = ({renderPart, resource, handleUserImageClick, zoomResource, scaleRatio, mode, resourceNumber, zoomResourceId}) => {
    const classes = useStyles ();
    const theme = useTheme();
    const {t} = useTranslation();
    const [showWarningDialog, setShowWarningDialog] = useState(false);

    const scaledRenderPart = useMemo(() => {
        return {
            width: renderPart.size.width * scaleRatio,
            height: renderPart.size.height * scaleRatio,
            x: renderPart.position.x * scaleRatio,
            y: renderPart.position.y * scaleRatio,
        }
    }, [renderPart, scaleRatio])

    const cropRect = useMemo(() => {
        if (!resource.image.uuid) {
            return null;
        }

        // If we have a picture position, we don't need to calc (Used when dragging)
        if (resource.image.picturePosition) {
            return null;
        }

        if (resource.image.cropRect) {
            const { x, y, width, height} = resource.image.cropRect;
            return {
                left: x,
                top: y,
                width,
                height
            };
        }

        const imageSize = getImageResourceSize(resource.image, renderPart);

        const pos = getBoxSize(renderPart.image.adaption, imageSize, scaledRenderPart);
        // Get crop rect for fill in:
        return calculateCropRect(pos, imageSize, scaledRenderPart)
        // Include render part as it might be scaled:
    }, [resource, renderPart, scaleRatio]);

    const picturePosition = useMemo( () => {
        if (!resource.image.uuid) {
            return null;
        }

        if (resource.image.picturePosition) {
            return resource.image.picturePosition
        }

        const imageSize = getImageResourceSize(resource.image, renderPart);

        return cropRectToImagePosition(cropRect, imageSize, scaledRenderPart, renderPart.image.adaption);
        // The renderpart might be scaled, so we also depend on that:
        }, [cropRect, resource, renderPart, scaleRatio])

    const qualityWarning = useMemo( () => {
        // During update, no warning
        if (resource?.image.picturePosition || !resource?.image.uuid) {
            return false;
        }

        const imagePixels = cropRect.width * cropRect.height;
        let imageArea = {
            width: renderPart.size.width,
            height: renderPart.size.height,
        };

        /* Actual pixels filled isn't really important?
        if (renderPart.image.adaption == "fit-in") {
            imageArea = {
                width: crop.rect.width < renderPart.size.width ? crop.rect.width :  renderPart.size.width,
                height: crop.rect.height < renderPart.size.height ? crop.rect.height :  renderPart.size.height,
            }
        }
         */

        const imageAreaPixels = imageArea.width * imageArea.height;

        return imageQualityWarning(imageAreaPixels, imagePixels);

    }, [cropRect, resource?.image.picturePosition]);


    const styles = {
        width: scaledRenderPart.width + 'px',
        height: scaledRenderPart.height + 'px',
        top: scaledRenderPart.y + 'px',
        left: scaledRenderPart.x + 'px',
    }

    let imageStyle = {
        position: 'absolute',
        // This is needed to get the proper placement. Don't remember why :/
        transform: 'translateZ(0)'
    };

    if (picturePosition) {
        imageStyle = {
            ...imageStyle,
            ...{
                width: parseInt(picturePosition.width) + 'px',
                height: parseInt(picturePosition.height) + 'px',
                top: parseInt(picturePosition.y) + 'px',
                left: parseInt(picturePosition.x) + 'px'
            }
        }
    }

    console.log('Dragging', zoomResourceId, resource.fullId);
    let selectText = '';
    if ((handleUserImageClick || zoomResource && zoomResource.fullId == resource.fullId) && !resource.image.uuid) {
        styles.backgroundColor = theme.palette.primary.main;
        if (mode != 'select-user-image') {
            selectText = t('Select image');
        }
    }
    else if (zoomResourceId && zoomResourceId != resource.fullId) {
        console.log('Resource is not selected', resource.fullId, zoomResourceId)
        styles.backgroundColor = '#999';
    }
    else {
        console.log('RenderUserImage', zoomResource, resource)
    }

    const effect = resource.image.overrideEffect || renderPart.image.effect || 'original'
    const rotation = resource.image.rotation;

    console.log('Render image resource', resource, qualityWarning);

    return <div style={styles} className={`${classes.renderPart} ${resource.image.loading ? 'loading' : ''}`}>
        {resource.image.uuid && <div style={imageStyle}>
            <EpmsImage style={{width: '100%', height: '100%'}} uuid={resource.image.uuid} effect={effect} rotation={rotation}
                       size={640}/>
        </div>
        }
        {selectText && <div className={classes.selectText}>{selectText}</div>}
        {mode == 'select-user-image' && <div className={classes.selectImage}>{resourceNumber}</div>}
        {qualityWarning && <IconButton className={classes.qualityWarning} onClick={() => setShowWarningDialog(true)}><Icon>warning</Icon></IconButton>}
        {showWarningDialog && <Dialog>test</Dialog>}
    </div>
}